import { App, AppType, PolicyType, Service } from '@Types/app';

export type ProvisionPrimaryApp =
  | App.Exchange
  | App.SharePoint
  | App.OneDrive
  | App.Box
  | App.Dropbox
  | App.GoogleDrive
  | App.SalesforceSandbox //deprecated
  | App.SalesforceProduction //deprecated
  | App.IdentityProtection
  | App.Gmail
  | App.GmailInline
  | App.Teams
  | App.TeamsChat
  | App.Mip
  | App.ExchangeInline
  | App.ExchangeServer
  | App.Mitigation
  | App.Soc
  | App.SocManagement
  | App.O365AuditLog
  | App.GraphNotification
  | App.Rms // deprecated
  | App.DeprecatedSharePointOneDrive //deprecated
  | App.AnomalyDetection; // deprecated

export type DelegatorAccountType = Exclude<
  AppType,
  | AppType.Rms
  | AppType.OneDriveBasic
  | AppType.SharePointBasic
  | AppType.TeamsBasic
  | AppType.ExchangeMailFlow
  | AppType.ExchangeInlineMailFlow
>;

//same as tm_company_plan
// Note: You may found there is policyType=11 in legacy dev db, that is deprecated EWS api token, which no long used anymore
export type CompanyPlanType =
  | PolicyType
  | AppType.ExchangeServer
  | AppType.Mip
  | AppType.IdentityProtection;

export interface ProvisionableEntityInfo {
  primaryApp: ProvisionPrimaryApp; // The primary app used to identify the entity for provision operations
  orderedProvisionApps: App[]; // Ordered list of apps for provision, affects provision sequence
  isMultiAppsProvision?: boolean; // Whether the entity has multiple apps provisioned, default is false
  // Note: If policyType is defined, the primaryApp should be the app associated with the policy type.
  // For example, if policyType is PolicyType.Exchange, primaryApp should be App.Exchange.
  // If policyType is not defined, primaryApp can be any app that represents the entity
  // (e.g., App.Mitigation, App.Soc, etc.).
  // This allows for flexibility in defining provisionable entities that may not have a direct policy association
  // while still being provisionable.
  policyType?: PolicyType; // Associated policy type, if applicable
  delegatorAccountType?: DelegatorAccountType; // delegated account type, used for tm_delegator_account
  companyPlanType?: CompanyPlanType; // company plan type, used for tm_company_plan
}
// Union type for all provisionable entities
type StandaloneProvisionableEntity = Extract<
  App,
  | App.Mitigation
  | App.Soc
  | App.SocManagement
  | App.O365AuditLog
  | App.GraphNotification
  | App.Rms
  | App.AnomalyDetection
  | App.DeprecatedSharePointOneDrive
>;

export type ProvisionEntity = Service | StandaloneProvisionableEntity;

// default we use first app as service's main app, but exchagne inline is special, it has two apps, so we use ExchangeInline as main app
const Service2Apps: {
  [key in Service]: ProvisionableEntityInfo;
} = {
  [Service.Exchange]: {
    primaryApp: App.Exchange,
    orderedProvisionApps: [App.Exchange],
    policyType: PolicyType.Exchange,
    delegatorAccountType: AppType.Exchange,
    companyPlanType: PolicyType.Exchange,
  },
  [Service.SharePoint]: {
    primaryApp: App.SharePoint,
    orderedProvisionApps: [App.SharePoint, App.SharePointBasic],
    isMultiAppsProvision: true, // sharepoint has two apps provisioned
    policyType: PolicyType.SharePoint,
    delegatorAccountType: AppType.SharePoint,
    companyPlanType: PolicyType.SharePoint,
  },
  [Service.OneDrive]: {
    primaryApp: App.OneDrive,
    orderedProvisionApps: [App.OneDrive, App.OneDriveBasic],
    isMultiAppsProvision: true, // onedrive has two apps provisioned
    policyType: PolicyType.OneDrive,
    delegatorAccountType: AppType.OneDrive,
    companyPlanType: PolicyType.OneDrive,
  },
  [Service.Box]: {
    primaryApp: App.Box,
    orderedProvisionApps: [App.Box],
    policyType: PolicyType.Box,
    delegatorAccountType: AppType.Box,
    companyPlanType: PolicyType.Box,
  },
  [Service.Dropbox]: {
    primaryApp: App.Dropbox,
    orderedProvisionApps: [App.Dropbox],
    policyType: PolicyType.Dropbox,
    delegatorAccountType: AppType.Dropbox,
    companyPlanType: PolicyType.Dropbox,
  },
  [Service.GoogleDrive]: {
    primaryApp: App.GoogleDrive,
    orderedProvisionApps: [App.GoogleDrive],
    policyType: PolicyType.GoogleDrive,
    delegatorAccountType: AppType.GoogleDrive,
    companyPlanType: PolicyType.GoogleDrive,
  },
  [Service.SalesforceSandbox]: {
    primaryApp: App.SalesforceSandbox,
    orderedProvisionApps: [App.SalesforceSandbox], // deprecated
    policyType: PolicyType.SalesforceSandbox,
    delegatorAccountType: AppType.SalesforceSandbox,
    companyPlanType: PolicyType.SalesforceSandbox,
  },
  [Service.SalesforceProduction]: {
    primaryApp: App.SalesforceProduction,
    orderedProvisionApps: [App.SalesforceProduction], // deprecated
    policyType: PolicyType.SalesforceProduction,
    delegatorAccountType: AppType.SalesforceProduction,
    companyPlanType: PolicyType.SalesforceProduction,
  },
  [Service.IdentityProtection]: {
    primaryApp: App.IdentityProtection,
    orderedProvisionApps: [App.IdentityProtection],
    delegatorAccountType: AppType.IdentityProtection,
    companyPlanType: AppType.IdentityProtection,
  },
  [Service.Gmail]: {
    primaryApp: App.Gmail,
    orderedProvisionApps: [App.Gmail],
    policyType: PolicyType.Gmail,
    delegatorAccountType: AppType.Gmail,
    companyPlanType: PolicyType.Gmail,
  },
  [Service.GmailInline]: {
    primaryApp: App.GmailInline,
    orderedProvisionApps: [App.GmailInline],
    policyType: PolicyType.GmailInline,
    delegatorAccountType: AppType.GmailInline,
    companyPlanType: PolicyType.GmailInline,
  },
  [Service.Teams]: {
    primaryApp: App.Teams,
    orderedProvisionApps: [App.Teams, App.TeamsBasic],
    isMultiAppsProvision: true, // teams has two apps provisioned
    policyType: PolicyType.Teams,
    delegatorAccountType: AppType.Teams,
    companyPlanType: PolicyType.Teams,
  },
  [Service.TeamsChat]: {
    primaryApp: App.TeamsChat,
    orderedProvisionApps: [App.TeamsChat],
    policyType: PolicyType.TeamsChat,
    delegatorAccountType: AppType.TeamsChat,
    companyPlanType: PolicyType.TeamsChat,
  },
  [Service.Mip]: {
    primaryApp: App.Mip,
    orderedProvisionApps: [App.Mip],
    delegatorAccountType: AppType.Mip,
    companyPlanType: AppType.Mip,
  },
  [Service.ExchangeInline]: {
    primaryApp: App.ExchangeInline,
    orderedProvisionApps: [App.ExchangeInlineMailFlow, App.ExchangeInline],
    isMultiAppsProvision: true, // exchange inline has two apps provisioned
    policyType: PolicyType.ExchangeInline,
    delegatorAccountType: AppType.ExchangeInline,
    companyPlanType: PolicyType.ExchangeInline,
  },
  [Service.ExchangeServer]: {
    primaryApp: App.ExchangeServer,
    orderedProvisionApps: [App.ExchangeServer],
    delegatorAccountType: AppType.ExchangeServer,
    companyPlanType: AppType.ExchangeServer,
  },
};
const StandaloneProvisionApps: {
  [key in StandaloneProvisionableEntity]: ProvisionableEntityInfo;
} = {
  [App.DeprecatedSharePointOneDrive]: {
    primaryApp: App.DeprecatedSharePointOneDrive, //deprecated SharePoint and OneDrive
    // DeprecatedSharePointOneDrive is a legacy app that combines SharePoint and OneDrive provisioning
    // It is not multi-app provisioned, as it is a standalone app
    // it is legacy logic, it has 2 apps provisioned, but we don't use it anymore, so to simplify the logic,
    // we treat it as a single app for provisioning purposes
    // it has 2 policy types, SharePoint and OneDrive, 2 company plan types
    orderedProvisionApps: [App.DeprecatedSharePointOneDrive],
    isMultiAppsProvision: false, // DeprecatedSharePointOneDrive is a standalone app,
    delegatorAccountType: AppType.DeprecatedSharePointOneDrive, // DeprecatedSharePointOneDrive has a delegator account
    // companyPlanTypes: [PolicyType.SharePoint, PolicyType.OneDrive],
    // policyTypes: [PolicyType.SharePoint, PolicyType.OneDrive],
  },
  [App.Mitigation]: {
    primaryApp: App.Mitigation,
    orderedProvisionApps: [App.Mitigation],
    isMultiAppsProvision: false, // Mitigation is a standalone app, not multi-app provisioned
    delegatorAccountType: AppType.Mitigation, // Mitigation has a delegator account
  },
  [App.Soc]: {
    primaryApp: App.Soc,
    orderedProvisionApps: [App.Soc],
    isMultiAppsProvision: false, // Soc is a standalone app, not multi-app provisioned
    delegatorAccountType: AppType.Soc, // Soc has a delegator account
  },
  [App.SocManagement]: {
    primaryApp: App.SocManagement,
    orderedProvisionApps: [App.SocManagement],
    isMultiAppsProvision: false, // SocManagement is a standalone app, not multi-app provisioned
    delegatorAccountType: AppType.SocManagement, // SocManagement has a delegator account
  },
  [App.O365AuditLog]: {
    primaryApp: App.O365AuditLog,
    orderedProvisionApps: [App.O365AuditLog],
    isMultiAppsProvision: false, // O365AuditLog is a standalone app, not multi-app provisioned
    delegatorAccountType: AppType.O365AuditLog, // O365AuditLog has a delegator account
  },
  [App.GraphNotification]: {
    primaryApp: App.GraphNotification,
    orderedProvisionApps: [App.GraphNotification],
    isMultiAppsProvision: false, // GraphNotification is a standalone app, not multi-app provisioned
    delegatorAccountType: AppType.GraphNotification, // GraphNotification has a delegator account
  },
  [App.Rms]: {
    //deprecated
    primaryApp: App.Rms,
    orderedProvisionApps: [App.Rms],
    isMultiAppsProvision: false, // Rms is a standalone app, not multi-app provisioned
  },
  [App.AnomalyDetection]: {
    //deprecated
    primaryApp: App.AnomalyDetection,
    orderedProvisionApps: [App.AnomalyDetection],
    isMultiAppsProvision: false, // AnomalyDetection is a standalone app, not multi-app provisioned
    delegatorAccountType: AppType.AnomalyDetection, // AnomalyDetection has a delegator account
  },
};

export const ProvisionEntity2Infos: {
  [key in ProvisionEntity]: ProvisionableEntityInfo;
} = {
  // Service-based provisions (from Service2Apps)
  ...Service2Apps,
  // Standalone app provisions (apps that can be provisioned but don't belong to a service)
  ...StandaloneProvisionApps,
};
